import { useState, useEffect } from 'react';
import {
  Box, Typography, Paper, Grid, CircularProgress, Card, CardContent,
  FormControl, InputLabel, Select, MenuItem, Alert,
  TableContainer, Table, TableHead, TableRow, TableCell, TableBody
} from '@mui/material';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
} from 'chart.js';
import { Bar, Pie } from 'react-chartjs-2';
import {
  fetchUserTrackingSessions,
  analyzeUserTrackingSessions,
  generateBillingPeriodOptions,
  UserAnalytics
} from '../services/userTrackingService';
import {
  fetchParkingBookings,
  analyzeParkingBookings,
  ParkingBookingsAnalytics
} from '../services/parkingBookingsService';

// Register ChartJS components
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const PRODUCT_FLAVORS = [
  { value: 'cargoTour', label: 'Cargo Tour' },
  { value: 'satlog', label: 'Satlog' },
];

export default function Analytics() {
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  // Get current billing period as default
  const getCurrentBillingPeriod = () => {
    const now = new Date();
    return `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, '0')}`;
  };

  const [billingPeriod, setBillingPeriod] = useState(getCurrentBillingPeriod());
  const [productFlavor, setProductFlavor] = useState('cargoTour');
  const [analytics, setAnalytics] = useState<UserAnalytics | null>(null);
  const [parkingAnalytics, setParkingAnalytics] = useState<ParkingBookingsAnalytics | null>(null);

  const billingPeriodOptions = generateBillingPeriodOptions();

  useEffect(() => {
    const fetchAnalyticsData = async () => {
      try {
        setLoading(true);
        setError(null);

        // Fetch user tracking sessions
        const sessions = await fetchUserTrackingSessions(billingPeriod, productFlavor);
        const analyticsData = analyzeUserTrackingSessions(sessions);
        setAnalytics(analyticsData);

        // Fetch parking bookings
        const parkingBookings = await fetchParkingBookings(billingPeriod);
        const parkingAnalyticsData = analyzeParkingBookings(parkingBookings);
        setParkingAnalytics(parkingAnalyticsData);
      } catch (err) {
        console.error('Error fetching analytics:', err);
        setError('Failed to load analytics data. Please try again.');
      } finally {
        setLoading(false);
      }
    };

    fetchAnalyticsData();
  }, [billingPeriod, productFlavor]);

  // Chart data for premium vs free users
  const premiumChartData = {
    labels: ['Premium Users', 'Free Users'],
    datasets: [
      {
        data: analytics ? [analytics.premiumUsers, analytics.freeUsers] : [0, 0],
        backgroundColor: [
          'rgba(76, 175, 80, 0.7)',
          'rgba(158, 158, 158, 0.7)',
        ],
        borderColor: [
          'rgba(76, 175, 80, 1)',
          'rgba(158, 158, 158, 1)',
        ],
        borderWidth: 2,
      }
    ],
  };

  // Chart data for subscription plans
  const subscriptionPlanLabels = analytics
    ? Object.keys(analytics.usersBySubscriptionPlan).map(plan =>
        plan.length > 30 ? plan.substring(0, 30) + '...' : plan
      )
    : [];

  const subscriptionPlanData = {
    labels: subscriptionPlanLabels,
    datasets: [
      {
        label: 'Active Users',
        data: analytics ? Object.values(analytics.usersBySubscriptionPlan) : [],
        backgroundColor: 'rgba(33, 150, 243, 0.7)',
        borderColor: 'rgba(33, 150, 243, 1)',
        borderWidth: 1,
      }
    ],
  };

  const formatBillingPeriodDisplay = (period: string) => {
    const [year, month] = period.split('-');
    const date = new Date(parseInt(year), parseInt(month) - 1);
    return date.toLocaleDateString('en-US', { month: 'long', year: 'numeric' });
  };

  return (
    <Box>
      <Typography variant="h4" gutterBottom>
        User Analytics
      </Typography>

      {/* Filters */}
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Grid container spacing={3} alignItems="center">
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Billing Period</InputLabel>
              <Select
                value={billingPeriod}
                label="Billing Period"
                onChange={(e) => setBillingPeriod(e.target.value)}
              >
                {billingPeriodOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <FormControl fullWidth>
              <InputLabel>Product</InputLabel>
              <Select
                value={productFlavor}
                label="Product"
                onChange={(e) => setProductFlavor(e.target.value)}
              >
                {PRODUCT_FLAVORS.map((flavor) => (
                  <MenuItem key={flavor.value} value={flavor.value}>
                    {flavor.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          <Grid item xs={12} md={4}>
            <Typography variant="body2" color="text.secondary">
              Showing data for {formatBillingPeriodDisplay(billingPeriod)}
            </Typography>
          </Grid>
        </Grid>
      </Paper>

      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}

      {loading ? (
        <Box display="flex" justifyContent="center" alignItems="center" minHeight="400px">
          <CircularProgress />
        </Box>
      ) : (
        <>
          {/* Summary Cards */}
          <Grid container spacing={3} sx={{ mb: 3 }}>
            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="primary" gutterBottom>
                    {analytics?.totalActiveUsers || 0}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Total Active Users
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" sx={{ color: 'success.main' }} gutterBottom>
                    {analytics?.premiumUsers || 0}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Premium Users
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
            <Grid item xs={12} md={4}>
              <Card elevation={3}>
                <CardContent sx={{ textAlign: 'center' }}>
                  <Typography variant="h3" color="text.secondary" gutterBottom>
                    {analytics?.freeUsers || 0}
                  </Typography>
                  <Typography variant="h6" color="text.secondary">
                    Free Users
                  </Typography>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Charts */}
          <Grid container spacing={3}>
            {/* Premium vs Free Chart */}
            <Grid item xs={12} md={5}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Premium vs Free Users
                  </Typography>
                  <Box height={300} display="flex" justifyContent="center" alignItems="center">
                    {analytics && analytics.totalActiveUsers > 0 ? (
                      <Pie
                        data={premiumChartData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          plugins: {
                            legend: {
                              position: 'bottom',
                            },
                            tooltip: {
                              callbacks: {
                                label: (context) => {
                                  const value = context.raw as number;
                                  const total = analytics.totalActiveUsers;
                                  const percentage = ((value / total) * 100).toFixed(1);
                                  return `${context.label}: ${value} (${percentage}%)`;
                                }
                              }
                            }
                          },
                        }}
                      />
                    ) : (
                      <Typography color="text.secondary">No data available</Typography>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Subscription Plan Chart */}
            <Grid item xs={12} md={7}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Users by Subscription Plan
                  </Typography>
                  <Box height={300}>
                    {analytics && Object.keys(analytics.usersBySubscriptionPlan).length > 0 ? (
                      <Bar
                        data={subscriptionPlanData}
                        options={{
                          responsive: true,
                          maintainAspectRatio: false,
                          indexAxis: 'y',
                          scales: {
                            x: {
                              beginAtZero: true,
                              ticks: {
                                stepSize: 1,
                              },
                            },
                          },
                          plugins: {
                            legend: {
                              display: false,
                            },
                          },
                        }}
                      />
                    ) : (
                      <Box display="flex" justifyContent="center" alignItems="center" height="100%">
                        <Typography color="text.secondary">No data available</Typography>
                      </Box>
                    )}
                  </Box>
                </CardContent>
              </Card>
            </Grid>

            {/* Detailed Table */}
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Subscription Plan Details
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Subscription Plan</TableCell>
                          <TableCell align="right">Active Users</TableCell>
                          <TableCell align="right">Percentage</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {analytics && Object.entries(analytics.usersBySubscriptionPlan)
                          .sort(([, a], [, b]) => b - a)
                          .map(([plan, count]) => {
                            const percentage = analytics.totalActiveUsers > 0
                              ? ((count / analytics.totalActiveUsers) * 100).toFixed(1)
                              : '0';
                            return (
                              <TableRow key={plan}>
                                <TableCell>
                                  <Typography
                                    variant="body2"
                                    sx={{
                                      fontFamily: 'monospace',
                                      fontSize: '0.85rem',
                                      wordBreak: 'break-all'
                                    }}
                                  >
                                    {plan}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{count}</TableCell>
                                <TableCell align="right">{percentage}%</TableCell>
                              </TableRow>
                            );
                          })}
                        {(!analytics || Object.keys(analytics.usersBySubscriptionPlan).length === 0) && (
                          <TableRow>
                            <TableCell colSpan={3} align="center">
                              <Typography color="text.secondary">No data available</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Grid>

          {/* Happy Trucker Parking Bookings Conversion Section */}
          <Box sx={{ mt: 4 }}>
            <Typography variant="h5" gutterBottom>
              Happy Trucker - Parking Bookings Conversions
            </Typography>

            {/* Summary Cards for Parking Bookings */}
            <Grid container spacing={3} sx={{ mb: 3 }}>
              <Grid item xs={12} md={6}>
                <Card elevation={3}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" color="primary" gutterBottom>
                      {parkingAnalytics?.totalBookings || 0}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      Total Parking Bookings
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
              <Grid item xs={12} md={6}>
                <Card elevation={3}>
                  <CardContent sx={{ textAlign: 'center' }}>
                    <Typography variant="h3" sx={{ color: 'success.main' }} gutterBottom>
                      {parkingAnalytics?.uniqueUsers || 0}
                    </Typography>
                    <Typography variant="h6" color="text.secondary">
                      Unique Users
                    </Typography>
                  </CardContent>
                </Card>
              </Grid>
            </Grid>

            {/* Bookings by Status Table */}
            <Grid item xs={12}>
              <Card elevation={3}>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Bookings by Status
                  </Typography>
                  <TableContainer>
                    <Table>
                      <TableHead>
                        <TableRow>
                          <TableCell>Status</TableCell>
                          <TableCell align="right">Total Bookings</TableCell>
                          <TableCell align="right">Unique Users</TableCell>
                          <TableCell align="right">% of Total Bookings</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {parkingAnalytics && Object.entries(parkingAnalytics.bookingsByStatus)
                          .sort(([, a], [, b]) => b.count - a.count)
                          .map(([status, data]) => {
                            const percentage = parkingAnalytics.totalBookings > 0
                              ? ((data.count / parkingAnalytics.totalBookings) * 100).toFixed(1)
                              : '0';
                            return (
                              <TableRow key={status}>
                                <TableCell>
                                  <Typography variant="body2" sx={{ fontWeight: 'medium' }}>
                                    {status}
                                  </Typography>
                                </TableCell>
                                <TableCell align="right">{data.count}</TableCell>
                                <TableCell align="right">{data.uniqueUsers}</TableCell>
                                <TableCell align="right">{percentage}%</TableCell>
                              </TableRow>
                            );
                          })}
                        {(!parkingAnalytics || Object.keys(parkingAnalytics.bookingsByStatus).length === 0) && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography color="text.secondary">No parking bookings data available</Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                </CardContent>
              </Card>
            </Grid>
          </Box>
        </>
      )}
    </Box>
  );
}
